import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useCart } from '../../../context/CartContext';
import { useQuickView } from '../../../context/QuickViewContext';

const packages = [
  {
    id: 1,
    name: 'The Starter Sparkle',
    tagline: 'Perfect for beginners ready to shine',
    classIncluded: 'Cocktail Fundamentals (3hrs)',
    kitIncluded: 'Essential Starter Kit',
    manualIncluded: "Beginner's Bartending Guide",
    extras: ['Welcome champagne', 'Recipe card set', 'Certificate of completion'],
    originalPrice: 407,
    price: 279,
    savings: 128,
    badge: 'Most Popular',
    accent: 'from-amber-400 to-yellow-300',
    accentText: 'text-amber-900',
    image: 'https://public.readdy.ai/ai/img_res/bd42da87fa687a1ff8bcac3bdc10cf14.jpg',
  },
  {
    id: 2,
    name: 'The Golden Hour',
    tagline: 'Elevate your craft to the next level',
    classIncluded: 'Advanced Mixology (4hrs)',
    kitIncluded: 'Professional Bartender Set',
    manualIncluded: 'The Complete Cocktail Bible',
    extras: ['Premium spirits tasting', 'Gold bar spoon engraved', 'VIP follow-up session', 'Lifetime recipe access'],
    originalPrice: 717,
    price: 489,
    savings: 228,
    badge: 'Best Value',
    accent: 'from-rose-400 to-pink-300',
    accentText: 'text-rose-900',
    image: 'https://readdy.ai/api/search-image?query=premium%20rose%20gold%20cocktail%20set%20with%20professional%20bartender%20tools%20and%20crystal%20mixing%20glass%20on%20dark%20velvet%20surface%20with%20rose%20petals%20and%20warm%20candlelight%2C%20luxury%20mixology%20equipment%2C%20glamorous%20feminine%20aesthetic&width=500&height=600&seq=pkg-golden-001&orientation=portrait',
  },
  {
    id: 3,
    name: 'The Diamond Experience',
    tagline: 'The ultimate luxury mixology journey',
    classIncluded: 'Private One-on-One (2hrs) + Advanced Mixology (4hrs)',
    kitIncluded: 'Molecular Mixology Set + Professional Set',
    manualIncluded: 'Complete 6-Manual Collection',
    extras: ['Private tasting dinner', 'Personalised cocktail menu', 'Lifetime mentorship access', 'Exclusive masterclass invites', 'Crystal cocktail glass set'],
    originalPrice: 1475,
    price: 899,
    savings: 576,
    badge: 'Exclusive',
    accent: 'from-amber-300 to-yellow-200',
    accentText: 'text-amber-900',
    image: 'https://readdy.ai/api/search-image?query=ultra%20luxury%20diamond%20themed%20cocktail%20experience%20with%20crystal%20decanters%20and%20gold%20bar%20tools%20on%20black%20marble%20with%20diamond%20sparkle%20effects%20and%20champagne%20bubbles%2C%20opulent%20mixology%20setup%2C%20high%20end%20glamorous%20photography&width=500&height=600&seq=pkg-diamond-001&orientation=portrait',
  },
  {
    id: 4,
    name: 'Hen\'s Party Glam Package',
    tagline: 'Celebrate in style with the girls',
    classIncluded: 'Hen\'s Party Experience (3hrs, up to 12 guests)',
    kitIncluded: 'Tropical Tiki Collection',
    manualIncluded: 'Seasonal Cocktails Collection',
    extras: ['Champagne tower welcome', 'Party decorations included', 'Photo booth props', 'Personalised cocktail menu'],
    originalPrice: 617,
    price: 419,
    savings: 198,
    badge: 'Party Fave',
    accent: 'from-pink-400 to-rose-300',
    accentText: 'text-pink-900',
    image: 'https://readdy.ai/api/search-image?query=glamorous%20hens%20party%20cocktail%20setup%20with%20pink%20champagne%20tower%20and%20rose%20gold%20accessories%20on%20elegant%20table%20with%20confetti%20and%20sparkle%20decorations%2C%20feminine%20celebration%20aesthetic%2C%20luxury%20party%20photography&width=500&height=600&seq=pkg-hens-001&orientation=portrait',
  },
  {
    id: 5,
    name: 'Couples\' Date Night Luxe',
    tagline: 'Romance meets mixology at your home',
    classIncluded: 'Home Bar Service for Couples (2.5hrs)',
    kitIncluded: 'Whiskey Cocktail Kit',
    manualIncluded: 'Classic Cocktails Mastery',
    extras: ['Candlelit setup at your home', 'Personalised couple\'s recipe book', 'Premium ingredient hamper', 'Rose petal presentation'],
    originalPrice: 857,
    price: 579,
    savings: 278,
    badge: 'Romantic',
    accent: 'from-red-400 to-rose-300',
    accentText: 'text-red-900',
    image: 'https://readdy.ai/api/search-image?query=romantic%20couples%20cocktail%20making%20experience%20with%20two%20elegant%20coupe%20glasses%20and%20whiskey%20set%20on%20candlelit%20table%20with%20rose%20petals%20and%20warm%20ambient%20lighting%2C%20intimate%20luxury%20date%20night%20aesthetic%2C%20premium%20photography&width=500&height=600&seq=pkg-couples-001&orientation=portrait',
  },
];

export default function SpecialPackages() {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: featuredRef, isVisible: featuredVisible } = useScrollAnimation({ rootMargin: '0px 0px -100px 0px' });
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ rootMargin: '0px 0px -50px 0px' });
  const { addToCart } = useCart();
  const { openQuickView } = useQuickView();

  const handleAddPackageToCart = (pkg: typeof packages[0]) => {
    addToCart({
      id: `package-${pkg.id}`,
      name: pkg.name,
      price: pkg.price,
      originalPrice: pkg.originalPrice,
      image: pkg.image,
      type: 'package',
      description: pkg.tagline,
    });
  };

  const handleQuickViewPackage = (pkg: typeof packages[0]) => {
    openQuickView({
      id: `package-${pkg.id}`,
      name: pkg.name,
      description: pkg.tagline,
      price: pkg.price,
      originalPrice: pkg.originalPrice,
      image: pkg.image,
      type: 'package',
      badge: pkg.badge,
      details: [
        { label: 'Class', value: pkg.classIncluded, icon: 'ri-graduation-cap-line' },
        { label: 'Kit', value: pkg.kitIncluded, icon: 'ri-box-3-line' },
        { label: 'Manual', value: pkg.manualIncluded, icon: 'ri-book-open-line' },
        { label: 'You Save', value: `$${pkg.savings}`, icon: 'ri-price-tag-3-line' },
      ],
      extras: pkg.extras,
    });
  };

  return (
    <section id="packages" className="relative py-28 overflow-hidden">
      {/* Glamorous background */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=dark%20luxurious%20abstract%20background%20with%20scattered%20gold%20dust%20particles%20and%20soft%20warm%20bokeh%20lights%20on%20deep%20black%20surface%2C%20elegant%20champagne%20gold%20sparkle%20texture%2C%20premium%20glamorous%20atmosphere&width=1920&height=1200&seq=pkg-bg-001&orientation=landscape"
          alt="Glamorous background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/80 to-black/90"></div>
      </div>

      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-rose-500/5 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-20 scroll-animate ${headerVisible ? 'visible' : ''}`}
        >
          <div className="inline-flex items-center gap-3 px-6 py-2.5 bg-amber-500/10 backdrop-blur-sm rounded-full border border-amber-500/20 mb-6">
            <i className="ri-sparkle-line text-amber-400"></i>
            <span className="text-amber-300 text-sm font-medium tracking-widest uppercase">Exclusive Bundles</span>
            <i className="ri-sparkle-line text-amber-400"></i>
          </div>
          <h2 className="text-4xl md:text-6xl font-serif font-bold text-white mb-5 leading-tight">
            Special <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-400">Packages</span>
          </h2>
          <p className="text-white/50 max-w-2xl mx-auto text-base leading-relaxed">
            Save big when you combine classes with premium cocktail kits and training manuals. Curated bundles designed for every occasion and skill level.
          </p>
          <div className="flex items-center justify-center gap-2 mt-6">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-amber-500/50"></div>
            <i className="ri-goblet-fill text-amber-500/40"></i>
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-amber-500/50"></div>
          </div>
        </div>

        {/* Featured Package - Diamond */}
        <div 
          ref={featuredRef}
          className={`mb-16 scroll-animate-scale ${featuredVisible ? 'visible' : ''}`}
        >
          <div className="relative bg-gradient-to-br from-white/[0.08] to-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 overflow-hidden group">
            <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
            <div className="grid lg:grid-cols-2 gap-0">
              <div className="relative h-80 lg:h-auto overflow-hidden">
                <img
                  src={packages[2].image}
                  alt={packages[2].name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/60 lg:block hidden"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent lg:hidden"></div>
                <div className="absolute top-6 left-6">
                  <span className="px-4 py-1.5 bg-gradient-to-r from-amber-400 to-yellow-300 text-amber-900 text-xs font-bold rounded-full uppercase tracking-wider">
                    {packages[2].badge}
                  </span>
                </div>
                {/* Quick View Button */}
                <button
                  onClick={() => handleQuickViewPackage(packages[2])}
                  className="absolute bottom-6 left-6 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full flex items-center gap-2 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 cursor-pointer hover:bg-white/40 text-white"
                >
                  <i className="ri-eye-line text-sm"></i>
                  <span className="text-xs font-semibold whitespace-nowrap">Quick View</span>
                </button>
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <p className="text-amber-400/70 text-sm tracking-widest uppercase mb-2">{packages[2].tagline}</p>
                <h3 className="text-3xl lg:text-4xl font-serif font-bold text-white mb-4">{packages[2].name}</h3>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-graduation-cap-line text-amber-400 text-xs"></i>
                    </div>
                    <p className="text-white/70 text-sm">{packages[2].classIncluded}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-box-3-line text-amber-400 text-xs"></i>
                    </div>
                    <p className="text-white/70 text-sm">{packages[2].kitIncluded}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-book-open-line text-amber-400 text-xs"></i>
                    </div>
                    <p className="text-white/70 text-sm">{packages[2].manualIncluded}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-8">
                  {packages[2].extras.map((extra, i) => (
                    <span key={i} className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-white/60 text-xs">
                      {extra}
                    </span>
                  ))}
                </div>

                <div className="flex items-end gap-4 mb-6">
                  <span className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-yellow-200">${packages[2].price}</span>
                  <span className="text-white/30 line-through text-lg mb-1">${packages[2].originalPrice}</span>
                  <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-semibold rounded-full mb-1">Save ${packages[2].savings}</span>
                </div>

                <button
                  onClick={() => handleAddPackageToCart(packages[2])}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-400 to-yellow-300 text-amber-900 font-bold rounded-full hover:from-amber-300 hover:to-yellow-200 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 hover:scale-[1.02] w-fit"
                >
                  <i className="ri-shopping-cart-line"></i>
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Package Cards Grid */}
        <div 
          ref={gridRef}
          className={`grid md:grid-cols-2 gap-6 stagger-children ${gridVisible ? 'visible' : ''}`}
        >
          {packages.filter((_, i) => i !== 2).map((pkg) => (
            <div
              key={pkg.id}
              className="group relative bg-gradient-to-br from-white/[0.06] to-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 overflow-hidden hover:border-amber-500/30 transition-all duration-500"
              onMouseEnter={() => setHoveredCard(pkg.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="flex flex-col sm:flex-row">
                <div className="relative w-full sm:w-48 h-52 sm:h-auto flex-shrink-0 overflow-hidden">
                  <img
                    src={pkg.image}
                    alt={pkg.name}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/40 hidden sm:block"></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent sm:hidden"></div>
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1 bg-gradient-to-r ${pkg.accent} ${pkg.accentText} text-xs font-bold rounded-full uppercase tracking-wider`}>
                      {pkg.badge}
                    </span>
                  </div>
                  {/* Quick View Button */}
                  <button
                    onClick={() => handleQuickViewPackage(pkg)}
                    className="absolute bottom-4 left-4 px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full flex items-center gap-1.5 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 cursor-pointer hover:bg-white/40 text-white"
                  >
                    <i className="ri-eye-line text-xs"></i>
                    <span className="text-[10px] font-semibold whitespace-nowrap">Quick View</span>
                  </button>
                </div>

                <div className="flex-1 p-6">
                  <p className="text-amber-400/60 text-xs tracking-widest uppercase mb-1">{pkg.tagline}</p>
                  <h3 className="text-xl font-serif font-bold text-white mb-3">{pkg.name}</h3>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2">
                      <i className="ri-graduation-cap-line text-amber-400/70 text-sm"></i>
                      <p className="text-white/50 text-xs">{pkg.classIncluded}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-box-3-line text-amber-400/70 text-sm"></i>
                      <p className="text-white/50 text-xs">{pkg.kitIncluded}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-book-open-line text-amber-400/70 text-sm"></i>
                      <p className="text-white/50 text-xs">{pkg.manualIncluded}</p>
                    </div>
                  </div>

                  <div className={`overflow-hidden transition-all duration-500 ${hoveredCard === pkg.id ? 'max-h-24 opacity-100 mb-4' : 'max-h-0 opacity-0'}`}>
                    <div className="flex flex-wrap gap-1.5">
                      {pkg.extras.map((extra, i) => (
                        <span key={i} className="px-2 py-0.5 bg-white/5 border border-white/10 rounded-full text-white/50 text-[10px]">
                          {extra}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-white/5">
                    <div className="flex items-end gap-2">
                      <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-yellow-200">${pkg.price}</span>
                      <span className="text-white/25 line-through text-sm mb-0.5">${pkg.originalPrice}</span>
                    </div>
                    <button
                      onClick={() => handleAddPackageToCart(pkg)}
                      className={`px-5 py-2 bg-gradient-to-r ${pkg.accent} ${pkg.accentText} text-xs font-bold rounded-full hover:scale-105 transition-all cursor-pointer whitespace-nowrap shadow-lg flex items-center gap-1`}
                    >
                      <i className="ri-shopping-cart-line text-xs"></i>
                      Add to Cart
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className={`mt-16 text-center scroll-animate ${gridVisible ? 'visible' : ''}`} style={{ transitionDelay: '400ms' }}>
          <p className="text-white/30 text-sm mb-4">Can&apos;t decide? Let us help you choose the perfect package.</p>
          <button
            onClick={() => {
              const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
              if (btn) btn.click();
            }}
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 border border-white/10 text-white/70 text-sm rounded-full hover:bg-white/10 hover:text-white transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-chat-smile-2-line"></i>
            Chat with Bella&apos;s Assistant
          </button>
        </div>
      </div>
    </section>
  );
}
